import type {
  PreSalesProjectIndexT,
  FormattedPreSalesProjectIndexT,
} from "rdx/modules/projects/types/projects/pre-sales";

export const isPreSalesIndexProject = <T extends object>(
  project: PreSalesProjectIndexT | T,
): project is PreSalesProjectIndexT => {
  return "projectId" in project && project.projectId !== undefined;
};

export const hasProjectTypeField = <T extends object>(row: T): row is Extract<T, { projectType: unknown }> =>
  "projectType" in row;

export const hasLeadIdField = <T extends object>(row: T): row is Extract<T, { leadId: unknown }> => "leadId" in row;

export const hasStageNameField = <T extends object>(row: T): row is Extract<T, { stageName: unknown }> =>
  "stageName" in row;

export const hasCurrentOfferField = <T extends object>(row: T): row is Extract<T, { hasCurrentOffer?: unknown }> =>
  "hasCurrentOffer" in row;

function formatPreSalesIndexData(preSalesProject: PreSalesProjectIndexT): FormattedPreSalesProjectIndexT;
function formatPreSalesIndexData<T extends object>(
  preSalesProject: PreSalesProjectIndexT | T,
): FormattedPreSalesProjectIndexT | Exclude<T, PreSalesProjectIndexT>;
function formatPreSalesIndexData<T extends object>(
  preSalesProject: PreSalesProjectIndexT | T,
): FormattedPreSalesProjectIndexT | T {
  if (!isPreSalesIndexProject(preSalesProject)) return preSalesProject;

  return {
    activities: preSalesProject.activities,
    address: {
      id: preSalesProject.addressId,
      street: preSalesProject.homeowner.address.addressStreet,
      unit: preSalesProject.homeowner.address.addressUnit,
      city: preSalesProject.addressCity,
      state: preSalesProject.addressState,
      zip: preSalesProject.homeowner.address.addressZip,
    },
    updatedAt: preSalesProject.coalescedUpdatedAt,
    contractOut: preSalesProject.contractOut,
    currentOfferId: preSalesProject.currentOfferId,
    currentOfferReceiverId: preSalesProject.currentOfferReceiverId,
    hasCurrentOffer: preSalesProject.hasCurrentOffer,
    hasExistingSystem: preSalesProject.hasExistingSystem,
    homeowner: {
      firstName: preSalesProject.homeowner.leadFirstName,
      lastName: preSalesProject.homeowner.leadLastName,
      address: {
        id: preSalesProject.addressId,
        street: preSalesProject.homeowner.address.addressStreet,
        unit: preSalesProject.homeowner.address.addressUnit,
        city: preSalesProject.addressCity,
        state: preSalesProject.addressState,
        zip: preSalesProject.homeowner.address.addressZip,
      },
      qualificationPercent: preSalesProject.leadQualificationPercent,
    },
    id: preSalesProject.projectId || preSalesProject.leadId,
    isUserLeadGen: preSalesProject.isUserLeadGen,
    isValidServiceArea: preSalesProject.isValidServiceArea,
    leadSource: preSalesProject.leadSource,
    homeownerInterest: preSalesProject.homeownerInterest,
    productTracks: preSalesProject.productTracks,
    latestProposalRejection: preSalesProject.latestProposalRejection,
    latestProposal: preSalesProject.latestProposal || null,
    lead: {
      id: preSalesProject.leadId,
      email: preSalesProject.leadEmail,
      phone: preSalesProject.leadPhone,
      firstName: preSalesProject.leadFirstName,
      lastName: preSalesProject.leadLastName,
      fullName: `${preSalesProject.leadFirstName} ${preSalesProject.leadLastName}`,
      kind: preSalesProject.leadKind,
      referrerId: preSalesProject.leadReferrerId,
      userId: preSalesProject.leadOwnerId,
      address: {
        id: preSalesProject.addressId,
        street: preSalesProject.homeowner.address.addressStreet,
        unit: preSalesProject.homeowner.address.addressUnit,
        city: preSalesProject.addressCity,
        state: preSalesProject.addressState,
        zip: preSalesProject.homeowner.address.addressZip,
      },
    },
    leadId: preSalesProject.leadId,
    enterpriseId: preSalesProject.enterpriseId,
    entOwnerId: preSalesProject.entOwnerId,
    email: preSalesProject.leadEmail,
    phone: preSalesProject.leadPhone,
    firstName: preSalesProject.leadFirstName,
    lastName: preSalesProject.leadLastName,
    leadQualificationPercent: preSalesProject.leadQualificationPercent,
    milestoneDisplayName: preSalesProject.milestoneDisplayName,
    offer: preSalesProject.offer,
    offerable: preSalesProject.offerable,
    passedStageNames: preSalesProject.passedStageNames,
    holdbackStrategyId: preSalesProject.holdbackStrategyId,
    managerId: preSalesProject.projectManagerId,
    mentorId: preSalesProject.projectMentorId,
    agentId: preSalesProject.projectSellerId,
    stageId: preSalesProject.projectStageId,
    milestoneName: preSalesProject.projectStageMilestoneName,
    stageName: preSalesProject.projectStageName,
    stageScore: preSalesProject.projectStageScore,
    status: preSalesProject.projectStatus,
    projectType: preSalesProject.projectType,
    projectUsers: preSalesProject.projectUsers,
    proposalUrl: preSalesProject.proposalUrl,
    qualifyAt: preSalesProject.qualifyAt,
    closeAt: preSalesProject.closeAt || null,
    siteId: preSalesProject.siteId,
    tickets: preSalesProject.tickets,
    upcomingStageNames: preSalesProject.upcomingStageNames,
    visionProposal: preSalesProject.visionProposal || null,
  };
}

export default formatPreSalesIndexData;
